// INDEX_SPLIT_B3_v1 (07.09.2026) -- Kasse, Zahlungen, Steuer, Buchhaltung, Gutscheine: KassePage, PosSystemPage, PaymentsPage, TaxDashboardPage, TaxConsultantManager, AccountingPage, VouchersPage
// 1:1 aus app.jsx verschoben (Reihenfolge unverändert), nur `export` ergänzt und Imports erzeugt (scripts/split_frontend.mjs).
import { AlertCircle, BarChart3, Check, CheckCircle, CreditCard, Download, Euro, Plus, Star, TrendingUp, Users, useEffect, useState } from "../core/vendor.js";
import { api } from "../core/api.js";
import { COLORS, DemoModeContext, appointmentsWithoutReceipt, formatDate, formatDateShort, today } from "../core/config.jsx";
import { Badge, Btn, Card, Input, Modal, StatCard } from "../core/ui.jsx";
import { Header } from "../layout/layout.jsx";


// ============================================================
// POS_SHEARLY_SSO_v1 -- PAGE: Shearly Kasse per SSO oeffnen.
// Nur erreichbar wenn Admin das Kassen-Modul fuer den Salon freigeschaltet hat
// (Feature-Flag pos_shearly aus /api/features, Spalte salons.pos_shearly_enabled).
// ============================================================
export const KassePage = () => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const openKasse = async () => {
    setBusy(true); setErr("");
    // Popup-Blocker-sicher: Fenster synchron im Klick oeffnen, Ziel-URL nach dem Fetch setzen
    const w = window.open("", "_blank");
    const r = await api.request("/salon/pos-shearly-sso", { method: "POST", body: "{}" });
    if (r && r.ok && r.data && r.data.url) {
      if (w) { w.location = r.data.url; } else { window.location.href = r.data.url; }
    } else {
      if (w) w.close();
      setErr((r && r.data && (r.data.error || r.data.message)) || "Kasse konnte nicht geöffnet werden. Bitte später erneut versuchen.");
    }
    setBusy(false);
  };

  return (
    <div>
      <Header title="Kasse" subtitle="Shearly Kasse — TSE-konformes Kassenmodul für deinen Salon" />
      <Card style={{ padding: 24, maxWidth: 560 }}>
        <div style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
          Die Shearly Kasse läuft unter <b>kasse.shearly.pro</b> und öffnet sich in einem neuen Tab.
          Du wirst automatisch mit deinem Shearly-Konto angemeldet — kein zweites Passwort nötig.
        </div>
        <Btn onClick={openKasse} disabled={busy}>{busy ? "Öffne Kasse…" : "Kasse öffnen"}</Btn>
        {err && (
          <div style={{ marginTop: 14, padding: "10px 14px", background: `${COLORS.danger}10`, border: `1px solid ${COLORS.danger}30`, borderRadius: 8, fontSize: 13, color: COLORS.danger }}>
            {err}
          </div>
        )}
        <div style={{ marginTop: 16, fontSize: 11, color: COLORS.textLight }}>
          Die Anmeldung gilt 8 Stunden. Kassieren und Bons sind für alle Mitarbeiter möglich; Tagesabschluss und Export nur für Inhaber.
        </div>
      </Card>
    </div>
  );
};

// ============================================================
// POS_TILLHUB_v1 -- PAGE: Kassensystem-Anbindung
// ============================================================
export const PosSystemPage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState("");
  const [provider, setProvider] = useState("tillhub");
  const [accountId, setAccountId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [syncEnabled, setSyncEnabled] = useState(false);

  const load = async () => {
    setLoading(true);
    const r = await api.request("/salon/pos-settings");
    if (r && r.ok && r.data) {
      setSettings(r.data);
      setProvider(r.data.provider || "tillhub");
      setAccountId(r.data.account_id || "");
      setSyncEnabled(!!r.data.sync_enabled);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setTestMsg("");
    const body = { provider, account_id: accountId, sync_enabled: syncEnabled };
    if (apiKey.trim()) body.api_key = apiKey.trim();
    const r = await api.request("/salon/pos-settings", { method: "PUT", body: JSON.stringify(body) });
    if (r && r.ok) {
      setApiKey(""); // ausblenden nach Save
      await load();
    } else {
      alert("Speichern fehlgeschlagen.");
    }
    setSaving(false);
  };

  const testConnection = async () => {
    setTesting(true);
    setTestMsg("Teste Verbindung…");
    const r = await api.request("/salon/pos-test-connection", { method: "POST", body: "{}" });
    if (r && r.ok && r.data && r.data.ok) {
      setTestMsg("✅ Verbindung erfolgreich! Kassensystem ist verbunden.");
      await load();
    } else {
      setTestMsg("❌ " + ((r && r.data && (r.data.error || r.data.message)) || "Verbindung fehlgeschlagen"));
    }
    setTesting(false);
  };

  if (loading) return <div><Header title="Kassensystem" subtitle="Anbindung an externe Kassen" /><Card style={{ padding: 40, textAlign: "center" }}>Lade…</Card></div>;

  const isConnected = settings && settings.has_api_key && !!settings.connected_at;

  return (
    <div>
      <Header title="Kassensystem" subtitle="Verbinde Shearly mit deiner externen Kasse — Termine, Zahlungen & Trinkgeld synchronisieren" />

      {/* Status-Card */}
      <Card style={{ marginBottom: 16, background: isConnected ? `${COLORS.success}10` : "var(--cardHover)", border: `1px solid ${isConnected ? COLORS.success + "40" : COLORS.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 28 }}>{isConnected ? "✅" : "🟡"}</div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <h3 style={{ margin: "0 0 2px", fontSize: 15, fontWeight: 700 }}>
              {isConnected ? `Verbunden mit ${(settings.provider || "").charAt(0).toUpperCase() + (settings.provider || "").slice(1)}` : "Noch nicht verbunden"}
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: COLORS.textLight }}>
              {isConnected
                ? <>Letzter Status: {settings.last_sync_status || "—"} · Verbunden am: {settings.connected_at?.slice(0, 10) || "—"}</>
                : <>Trag deinen API-Key ein → Verbindung testen → fertig</>}
            </p>
          </div>
          {settings && settings.has_api_key && (
            <Btn size="sm" variant="secondary" onClick={testConnection} disabled={testing}>
              {testing ? "Teste…" : "Verbindung testen"}
            </Btn>
          )}
        </div>
        {testMsg && <div style={{ marginTop: 10, padding: "8px 12px", borderRadius: 8, fontSize: 12, background: "var(--card)", border: `1px solid ${COLORS.border}` }}>{testMsg}</div>}
      </Card>

      {/* Provider-Auswahl */}
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700 }}>Provider</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {[
            { id: "sumup", label: "SumUp", status: "verfügbar · empfohlen für Solo-Salons", color: COLORS.success, enabled: true },
            { id: "tillhub", label: "Tillhub", status: "verfügbar", color: COLORS.success, enabled: true },
            { id: "bcassa", label: "Bcassa", status: "verfügbar", color: COLORS.success, enabled: true },
            { id: "lightspeed", label: "Lightspeed", status: "in Vorbereitung", color: COLORS.textLight, enabled: false },
            { id: "ready2order", label: "ready2order", status: "in Vorbereitung", color: COLORS.textLight, enabled: false },
            { id: "helcim", label: "Helcim", status: "in Vorbereitung", color: COLORS.textLight, enabled: false },
          ].map(p => (
            <button key={p.id} onClick={() => p.enabled && setProvider(p.id)}
              disabled={!p.enabled}
              style={{
                padding: "10px 14px", borderRadius: 10, fontSize: 13, cursor: p.enabled ? "pointer" : "not-allowed",
                background: provider === p.id ? `${COLORS.primary}15` : "var(--cardHover)",
                color: provider === p.id ? COLORS.primary : COLORS.textLight,
                border: `1px solid ${provider === p.id ? COLORS.primary : COLORS.border}`,
                fontWeight: provider === p.id ? 700 : 500,
                opacity: p.enabled ? 1 : 0.6,
                display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, minWidth: 140,
              }}>
              <span>{p.label}</span>
              <span style={{ fontSize: 10, color: p.color }}>{p.status}</span>
            </button>
          ))}
        </div>
      </Card>

      {/* Provider-Setup (SumUp / Tillhub / Bcassa) */}
      {(provider === "sumup" || provider === "tillhub" || provider === "bcassa") && (
        <Card style={{ marginBottom: 16 }}>
          <h3 style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 700 }}>
            {provider === "sumup" ? "SumUp" : provider === "tillhub" ? "Tillhub" : "Bcassa"} verbinden
          </h3>
          {provider === "sumup" && (
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 14px", lineHeight: 1.6 }}>
              Du brauchst einen SumUp-Business-Account (<a href="https://sumup.com" target="_blank" style={{ color: COLORS.primary }}>sumup.com</a>) und einen API-Key.
              <br />
              <strong>So findest du den API-Key:</strong> <a href="https://developer.sumup.com" target="_blank" style={{ color: COLORS.primary }}>developer.sumup.com</a> → <em>Einloggen → API Keys → New Personal Key</em> mit Scopes <code>transactions.history.read</code> + <code>payments</code>.
              <br />
              <strong>Merchant-Code:</strong> in deinem SumUp-Dashboard unter <em>Profil → Mein Konto → Merchant Code</em> (z.B. MQXXXXXX).
              <br />
              <span style={{ fontSize: 11, color: COLORS.info }}>
                ℹ Sobald Test-Verbindung OK ist, wird ein Webhook-Secret erzeugt — den trägst du dann in deinem SumUp-Dashboard unter <em>Webhooks</em> ein, damit Zahlungen automatisch in Shearly als „bezahlt" markiert werden.
              </span>
            </p>
          )}
          {provider === "tillhub" && (
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 14px", lineHeight: 1.6 }}>
              Du brauchst dafür einen Tillhub-Account (<a href="https://www.tillhub.de" target="_blank" style={{ color: COLORS.primary }}>tillhub.de</a>) und einen API-Key.
              <br />
              <strong>So findest du den API-Key:</strong> Tillhub-Dashboard → <em>Verwaltung → API-Schlüssel → Neuen Schlüssel erstellen</em> mit Berechtigungen für „Transactions" + „Customers" lesen.
              <br />
              <strong>Account-ID:</strong> in Tillhub unter <em>Verwaltung → Konto → Account-ID</em> (Format: UUID).
            </p>
          )}
          {provider === "bcassa" && (
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 14px", lineHeight: 1.6 }}>
              Du brauchst dafür einen Bcassa-Account (<a href="https://bcassa.com" target="_blank" style={{ color: COLORS.primary }}>bcassa.com</a>) und einen API-Token.
              <br />
              <strong>So findest du den API-Token:</strong> Bcassa-Dashboard → <em>Einstellungen → API / Schnittstellen → Token erstellen</em> mit Lese-Rechten für Transaktionen + Kunden.
              <br />
              <strong>Account-ID:</strong> in Bcassa unter <em>Einstellungen → Konto → Mandanten-ID</em>.
              <br />
              <span style={{ fontSize: 11, color: COLORS.warning }}>
                ⚠ Bcassa-API noch in Beta-Test bei uns — wenn die Test-Verbindung „in Vorbereitung" zeigt, sind deine Daten gespeichert und ich aktiviere den Sync sobald wir's mit deinem Account verifiziert haben.
              </span>
            </p>
          )}

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>
              {provider === "sumup" ? "SumUp Merchant-Code" : provider === "bcassa" ? "Bcassa Mandanten-ID" : "Tillhub Account-ID"} *
            </label>
            <input value={accountId} onChange={e => setAccountId(e.target.value)}
              placeholder={provider === "sumup" ? "z.B. MQXXXXXX" : "z.B. 1234abcd-..."}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, background: "var(--card)", color: COLORS.text, boxSizing: "border-box", fontFamily: "monospace" }} />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 }}>
              API-Key {settings && settings.has_api_key ? <span style={{ fontWeight: 400, color: COLORS.success }}>(schon gespeichert — leer lassen zum Behalten)</span> : "*"}
            </label>
            <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)}
              placeholder={settings && settings.has_api_key ? "•••••••••• (gespeichert, leer = behalten)" : provider === "sumup" ? "Dein SumUp Personal Access Token" : provider === "bcassa" ? "Dein Bcassa API-Token" : "Dein Tillhub API-Key"}
              autoComplete="new-password"
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, background: "var(--card)", color: COLORS.text, boxSizing: "border-box", fontFamily: "monospace" }} />
            <p style={{ fontSize: 10, color: COLORS.textLight, margin: "4px 0 0" }}>Wird verschlüsselt gespeichert. Wir loggen niemals den Key.</p>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", padding: "8px 0" }}>
            <input type="checkbox" checked={syncEnabled} onChange={e => setSyncEnabled(e.target.checked)} disabled={!isConnected} />
            <strong>Auto-Sync aktivieren</strong>
            <span style={{ fontSize: 11, color: COLORS.textLight }}>{!isConnected ? "(erst verbinden + testen)" : "(jede Stunde)"}</span>
          </label>

          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <Btn onClick={save} disabled={saving || !accountId.trim() || (!settings?.has_api_key && !apiKey.trim())}>
              {saving ? "Speichert…" : "Speichern"}
            </Btn>
          </div>
        </Card>
      )}

      {/* Was die Anbindung bringt */}
      <Card>
        <h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 700 }}>Was bringt die Anbindung?</h3>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: COLORS.text, lineHeight: 1.8 }}>
          <li><strong>Termine als Buchung in der Kasse:</strong> Jeder abgeschlossene Termin landet automatisch als Verkaufsvorgang in Tillhub — Bargeld + Karte + Trinkgeld</li>
          <li><strong>DATEV-Export</strong> wird endlich vollständig (kommt aus deiner Kasse, nicht von Shearly geschätzt)</li>
          <li><strong>Provision-Berechnung</strong> wird genau (basiert auf echtem Umsatz pro Mitarbeiter, nicht auf Termin-Schätzungen)</li>
          <li><strong>Trinkgeld pro Mitarbeiter</strong> wird real abgebildet (nicht mehr „aus Online-Vorkasse geschätzt")</li>
          <li><strong>Kunden-Stamm-Sync:</strong> Stammkunden in Shearly = Stammkunden in Kasse, kein doppeltes Pflegen</li>
        </ul>
      </Card>
    </div>
  );
};

// ============================================================
// PAGE: Online-Vorkasse (vorher "Zahlungen" — POS_TILLHUB_v1: umbenannt)
// ============================================================
export const PaymentsPage = ({ appointments, setAppointments, customers, services, enabledFeatures }) => {
  const demoMode = React.useContext(DemoModeContext);
  // NO_RECEIPT_CHECK_v1 -- Salons mit Shearly Kasse: Kontrolle „Termine ohne Bon" statt „Offene Zahlungen"
  const posShearly = !!(enabledFeatures && enabledFeatures.pos_shearly === true);
  const noReceipt = posShearly ? appointmentsWithoutReceipt(appointments, 7) : { active: false, since: null, items: [] };
  const paid = appointments.filter(a => a.paymentStatus === "paid");
  const pending = appointments.filter(a => a.paymentStatus === "pending" && a.status !== "cancelled");
  const totalPaid = paid.reduce((s, a) => s + a.totalPrice, 0);
  const totalPending = pending.reduce((s, a) => s + a.totalPrice, 0);

  const markAsPaid = (aptId, method) => {
    setAppointments(prev => prev.map(a =>
      a.id === aptId ? { ...a, paymentStatus: "paid", paymentMethod: method } : a
    ));
    // Also update via API
    api.request(`/appointments/${aptId}`, {
      method: "PUT",
      body: JSON.stringify({ payment_status: "paid", payment_method: method }),
    });
  };

  /* BOOKING_PREPAY_v1 -- echte Online-Vorkasse (SumUp-Checkout des Salons) aus der Online-Buchung */
  const prepayList = appointments
    .filter(a => a.prepayStatus)
    .sort((a, b) => (b.date + (b.startTime || "")).localeCompare(a.date + (a.startTime || "")))
    .slice(0, 50);
  const fmtEur = (v) => (Number(v) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
  const prepaySum = prepayList.filter(a => a.prepayStatus === "paid").reduce((s, a) => s + (Number(a.prepaidAmount) || 0), 0);
  const prepayLabel = (a) => a.prepayStatus === "paid" ? { t: "bezahlt", c: COLORS.success }
    : a.prepayStatus === "pending" ? { t: "ausstehend", c: COLORS.warning }
    : a.prepayStatus === "refunded" ? { t: "erstattet", c: COLORS.textLight }
    : { t: "nicht bezahlt (Frist abgelaufen)", c: COLORS.textLight };

  return (
    <div>
      <Header title="Online-Vorkasse" subtitle="Vorkasse aus der Online-Buchung läuft über deinen SumUp-Checkout — Bargeld und Karte im Salon über dein Kassensystem" />
      {/* POS_TILLHUB_v1 -- Hinweis-Banner (BOOKING_PREPAY_v1: Text auf SumUp-Vorkasse umgestellt) */}
      <Card style={{ marginBottom: 16, background: "var(--cardHover)", borderLeft: `3px solid ${COLORS.primary}`, padding: "12px 16px" }}>
        <p style={{ margin: 0, fontSize: 13, color: COLORS.text, lineHeight: 1.6 }}>
          ℹ️ <strong>Online-Vorkasse:</strong> Ab dem eingestellten Leistungswert zahlen Kunden schon bei der Online-Buchung über dein SumUp- oder PayPal-Konto (Einstellungen → No-Show-Regeln). Das Geld geht direkt an dich. Barzahlungen + Karten-Zahlungen im Salon werden in deinem Kassensystem erfasst.
        </p>
      </Card>
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>💳 Online-Vorkasse</span>
          <span style={{ fontSize: 13, color: COLORS.success }}>{fmtEur(prepaySum)} eingegangen</span>
        </h3>
        {prepayList.length === 0 ? (
          <div style={{ fontSize: 13, color: COLORS.textLight }}>Noch keine Vorkasse-Zahlungen. Sobald ein Kunde bei der Online-Buchung über SumUp vorauszahlt, erscheint der Termin hier.</div>
        ) : prepayList.map(a => {
          const cust = customers.find(c => c.id === a.customerId);
          const lab = prepayLabel(a);
          return (
            <div key={a.id} style={{ padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{cust?.name || a.customerName || "Kunde"}{a.status === "cancelled" ? <span style={{ color: COLORS.danger, fontWeight: 500 }}> · storniert</span> : null}</div>
                <div style={{ fontSize: 12, color: COLORS.textLight }}>{formatDateShort(a.date)} {a.startTime} · Leistungswert {fmtEur(a.totalPrice)} · {a.prepayProvider === "paypal" ? "PayPal" : "SumUp"}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{fmtEur(a.prepaidAmount)}</div>
                <div style={{ fontSize: 11.5, color: lab.c, fontWeight: 600 }}>{lab.t}{a.prepayStatus === "refunded" && a.prepayRefundAmount != null ? ` ${fmtEur(a.prepayRefundAmount)}` : ""}</div>
              </div>
            </div>
          );
        })}
      </Card>
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: window.innerWidth < 768 ? 8 : 16, marginBottom: window.innerWidth < 768 ? 16 : 24 }}>
        <StatCard icon={CheckCircle} label="Bezahlt" value={`${totalPaid} €`} color={COLORS.success} />
        <StatCard icon={AlertCircle} label="Offen" value={`${totalPending} €`} color={COLORS.warning} />
        <StatCard icon={CreditCard} label="Kartenzahlung" value={`${paid.filter(a => a.paymentMethod === "card").length}`} color={COLORS.info} />
        <StatCard icon={Euro} label="Barzahlung" value={`${paid.filter(a => a.paymentMethod === "cash").length}`} color={COLORS.accent} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 16 }}>
        {posShearly ? (
          /* NO_RECEIPT_CHECK_v1 -- Kassen-Salons: hier NICHT „Karte / Bar erhalten" anbieten (das setzt bezahlt
             ohne Bon, an der TSE vorbei), sondern zeigen, zu welchen Terminen die Kasse keinen Bon gemeldet hat.
             Kassiert wird in der Kasse; die Seite markiert nichts. */
          <Card>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Termine ohne Bon <span style={{ fontWeight: 500, color: COLORS.textLight, fontSize: 12 }}>letzte 7 Tage</span></h3>
            {!noReceipt.active ? (
              <div style={{ padding: "16px 0", color: COLORS.textLight, fontSize: 13, lineHeight: 1.6 }}>
                Die Kontrolle startet, sobald der erste Termin über die Shearly Kasse kassiert wurde.
              </div>
            ) : noReceipt.items.length === 0 ? (
              <div style={{ textAlign: "center", padding: 30, color: COLORS.textLight }}>
                <CheckCircle size={32} color={COLORS.success} style={{ marginBottom: 8 }} />
                <div>Zu jedem Termin der letzten 7 Tage gibt es einen Bon.</div>
              </div>
            ) : (
              <>
                <p style={{ margin: "0 0 8px", fontSize: 12.5, color: COLORS.textLight, lineHeight: 1.6 }}>
                  Laut Kalender haben diese Termine stattgefunden, aber die Kasse hat keinen Bon dazu gemeldet. Häufige Gründe: frei kassiert, ohne den Termin in der Kasse anzutippen · Kundin nicht erschienen (dann im Kalender mit dem Grund „nicht erschienen“ stornieren) · in Pro als bezahlt markiert statt in der Kasse kassiert.
                </p>
                {noReceipt.items.map(apt => {
                  const cust = customers.find(c => c.id === apt.customerId);
                  const viaPro = apt.paymentStatus === "paid" && apt.paymentMethod && apt.paymentMethod !== "online";
                  const prepaid = !viaPro && (apt.prepayStatus === "paid" || apt.paymentMethod === "online");
                  return (
                    <div key={apt.id} style={{ padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{cust?.name || apt.customerName || "Kunde"}</div>
                        <div style={{ fontSize: 12, color: COLORS.textLight }}>{formatDateShort(apt.date)} {apt.startTime}{apt.employeeName ? ` · ${apt.employeeName}` : ""}</div>
                        {viaPro && <div style={{ fontSize: 11.5, color: COLORS.warning, marginTop: 2 }}>In Pro als bezahlt markiert – ohne Bon aus der Kasse</div>}
                        {prepaid && <div style={{ fontSize: 11.5, color: COLORS.textLight, marginTop: 2 }}>Online-Vorkasse bezahlt – in der Kasse noch nicht abgerechnet</div>}
                      </div>
                      <span style={{ fontWeight: 700, fontSize: 15, flexShrink: 0, whiteSpace: "nowrap" }}>{fmtEur(apt.totalPrice)}</span>
                    </div>
                  );
                })}
              </>
            )}
          </Card>
        ) : (
          <Card>
            <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Offene Zahlungen</h3>
            {pending.length === 0 ? (
              <div style={{ textAlign: "center", padding: 30, color: COLORS.textLight }}>
                <CheckCircle size={32} color={COLORS.success} style={{ marginBottom: 8 }} />
                <div>Alle Zahlungen beglichen</div>
              </div>
            ) : (
              pending.map(apt => {
                const cust = customers.find(c => c.id === apt.customerId);
                return (
                  <div key={apt.id} style={{ padding: "12px 0", borderBottom: `1px solid ${COLORS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{cust?.name}</div>
                      <div style={{ fontSize: 12, color: COLORS.textLight }}>{formatDateShort(apt.date)} {apt.startTime}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontWeight: 700, fontSize: 15 }}>{apt.totalPrice} €</span>
                      <Btn size="sm" variant="success" onClick={() => markAsPaid(apt.id, "card")}><CreditCard size={12} /> Karte</Btn>
                      <Btn size="sm" variant="secondary" onClick={() => markAsPaid(apt.id, "cash")}><Euro size={12} /> Bar erhalten</Btn>
                    </div>
                  </div>
                );
              })
            )}
          </Card>
        )}

        <Card>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Zahlungsübersicht</h3>
          <div style={{ fontSize: 13, lineHeight: 2.4 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Kartenzahlungen</span>
              <strong>{paid.filter(a => a.paymentMethod === "card").reduce((s, a) => s + a.totalPrice, 0)} €</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Barzahlungen</span>
              <strong>{paid.filter(a => a.paymentMethod === "cash").reduce((s, a) => s + a.totalPrice, 0)} €</strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: `2px solid ${COLORS.border}`, paddingTop: 8, marginTop: 8 }}>
              <span style={{ fontWeight: 700 }}>Gesamt bezahlt</span>
              <strong style={{ color: COLORS.success }}>{totalPaid} €</strong>
            </div>
            {posShearly ? (
              /* NO_RECEIPT_CHECK_v1.1 -- Kassen-Salons: „Noch offen" summierte JEDEN unbezahlten Termin (auch
                 zukuenftige) und widersprach damit der Liste daneben -> hier deren Summe */
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 700 }}>Ohne Bon (7 Tage)</span>
                <strong style={{ color: noReceipt.items.length ? COLORS.warning : COLORS.textLight }}>{noReceipt.active ? fmtEur(noReceipt.items.reduce((sum, a) => sum + (Number(a.totalPrice) || 0), 0)) : "—"}</strong>
              </div>
            ) : (
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ fontWeight: 700 }}>Noch offen</span>
                <strong style={{ color: COLORS.warning }}>{totalPending} €</strong>
              </div>
            )}
          </div>
          <div style={{ marginTop: 16, padding: 12, background: `${COLORS.info}08`, borderRadius: 8, fontSize: 12, color: COLORS.textLight }}>
            {/* NO_RECEIPT_CHECK_v1.1 -- Kassen-Salons haben den „Bar erhalten"-Knopf nicht mehr */}
            {posShearly
              ? "Bar und Karte kassierst du in der Shearly Kasse. Der Bon meldet den Termin hier automatisch als bezahlt."
              : 'Kartenzahlungen über Stripe werden automatisch erfasst. Barzahlungen bitte mit dem "Bar erhalten"-Button bestätigen.'}
          </div>
        </Card>
      </div>
    </div>
  );
};

// ============================================================
// TAX_CONSULTANT_v1 -- Eigene Read-Only-Page fuer Steuerberater-Login.
// Zeigt Termin-Liste (Plausibilitaet gegen Kasse) + Provisions-Report (Lohn-Vorbereitung).
// KEINE Kunden-Stammdaten, KEINE SMS-Logs, KEINE KI-Anrufe (DSGVO-Minimierung).
export const TaxDashboardPage = ({ currentUser }) => {
  const [tab, setTab] = useState("appointments"); // "appointments" | "commissions"
  const today = new Date();
  // BLOCKER_SERIES_TZ_FIX_v1 -- lokal formatieren: toISOString() machte aus dem Monatsersten den letzten Tag des Vormonats
  const defaultFrom = formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
  const defaultTo = formatDate(today);
  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(defaultTo);
  const [month, setMonth] = useState(today.toISOString().slice(0,7));
  const [apptData, setApptData] = useState(null);
  const [commData, setCommData] = useState(null);
  const [loading, setLoading] = useState(false);
  // TAX_CONSULTANT_KASSE_v1 (08.09.2026) -- Kassen-Einstieg + Anleitung für die Steuerberatung
  const [kasseBusy, setKasseBusy] = useState(false);
  const [kasseErr, setKasseErr] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const openKasse = async () => {
    setKasseBusy(true); setKasseErr("");
    // Popup-Blocker-sicher: Fenster synchron im Klick öffnen, Ziel-URL nach dem Fetch setzen (wie KassePage)
    const w = window.open("", "_blank");
    const r = await api.request("/salon/pos-shearly-sso", { method: "POST", body: "{}" });
    if (r && r.ok && r.data && r.data.url) {
      if (w) { w.location = r.data.url; } else { window.location.href = r.data.url; }
    } else {
      if (w) w.close();
      setKasseErr((r && r.data && (r.data.error || r.data.message)) || "Kasse konnte nicht geöffnet werden. Bitte später erneut versuchen.");
    }
    setKasseBusy(false);
  };

  const loadAppts = () => {
    setLoading(true);
    api.request(`/tax/appointments?from=${dateFrom}&to=${dateTo}`).then(r => {
      if (r && r.ok) setApptData(r.data);
      else setApptData({ items: [], total: 0, error: (r && r.data && r.data.error) || "Fehler" });
      setLoading(false);
    }).catch(() => { setLoading(false); setApptData({ items: [], total: 0, error: "Netzwerk-Fehler" }); });
  };
  const loadComm = () => {
    setLoading(true);
    api.request(`/tax/commissions?month=${month}`).then(r => {
      if (r && r.ok) setCommData(r.data);
      else setCommData({ items: [], error: (r && r.data && r.data.error) || "Fehler" });
      setLoading(false);
    }).catch(() => { setLoading(false); setCommData({ items: [], error: "Netzwerk-Fehler" }); });
  };
  useEffect(() => { if (tab === "appointments") loadAppts(); else loadComm(); }, [tab]);

  const exportCsv = () => {
    if (tab === "appointments" && apptData?.items) {
      const rows = [
        ["Datum","Uhrzeit","Mitarbeiter","Dauer (Min)","Brutto EUR","Status","Zahlung","Methode"],
        ...apptData.items.map(a => [a.date, a.start_time, a.employee, a.duration_min, a.total_eur, a.status, a.payment_status, a.payment_method])
      ];
      const csv = rows.map(r => r.map(c => `"${String(c||"").replace(/"/g,'""')}"`).join(";")).join("\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `shearly_termine_${dateFrom}_${dateTo}.csv`; a.click();
      URL.revokeObjectURL(url);
    } else if (tab === "commissions" && commData?.items) {
      const rows = [
        ["Mitarbeiter","Anzahl Termine","Umsatz EUR","Provisions-Satz %","Provision EUR"],
        ...commData.items.map(c => [c.name, c.appointments, c.total_eur, c.commission_rate_pct, c.commission_eur])
      ];
      const csv = rows.map(r => r.map(c => `"${String(c||"").replace(/"/g,'""')}"`).join(";")).join("\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `shearly_provisionen_${month}.csv`; a.click();
      URL.revokeObjectURL(url);
    }
  };

  return (
    <div>
      <Header title="Steuerberater-Bereich" subtitle="Termine + Provisions-Reports für Lohn & Plausibilitätsprüfung" />

      <Card style={{ marginBottom: 16, padding: "14px 18px", background: "rgba(59,130,246,0.06)", borderLeft: `3px solid ${COLORS.info}` }}>
        <p style={{ margin: 0, fontSize: 13, color: COLORS.text, lineHeight: 1.6 }}>
          <strong>Read-Only-Zugang</strong> für die Steuerberatung. Hier stehen Termine, Service-Daten und Provisions-Reports.
          <br/>
          <strong>Belege, Kassenbuch und DATEV-Export</strong> liegen in der Kasse des Salons. Nutzt der Salon die Shearly Kasse, geht es unten über „Kasse öffnen“ direkt dorthin (nur lesend).
        </p>
      </Card>

      {/* TAX_CONSULTANT_KASSE_v1 -- Kassen-Einstieg + Anleitung */}
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 900 ? "1fr" : "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <Card style={{ padding: "16px 18px" }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Shearly Kasse</div>
          <div style={{ fontSize: 13, color: COLORS.textLight, lineHeight: 1.6, marginBottom: 12 }}>
            Öffnet <b>kasse.shearly.pro</b> in einem neuen Tab, angemeldet als Steuerberater/in. Dort: Reiter <b>Export</b> (Kassenbuch &amp; DATEV, DSFinV-K, Inventurlisten, Langzeit-Archiv) und <b>Meldedaten</b>. Kein Kassieren, kein Abschluss.
          </div>
          <Btn onClick={openKasse} disabled={kasseBusy}>{kasseBusy ? "Öffne Kasse…" : "Kasse öffnen"}</Btn>
          {kasseErr && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: `${COLORS.danger}10`, border: `1px solid ${COLORS.danger}30`, borderRadius: 8, fontSize: 13, color: COLORS.danger }}>
              {kasseErr}
            </div>
          )}
          <div style={{ marginTop: 12, fontSize: 11, color: COLORS.textLight }}>
            Die Anmeldung gilt 8 Stunden. Falls der Salon eine andere Kasse nutzt, erscheint hier ein Hinweis.
          </div>
        </Card>
        <Card style={{ padding: "16px 18px" }}>
          <div onClick={() => setGuideOpen(!guideOpen)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Anleitung für die Steuerberatung</div>
            <span style={{ fontSize: 12, color: COLORS.primary, fontWeight: 600 }}>{guideOpen ? "Einklappen" : "Anzeigen"}</span>
          </div>
          {!guideOpen && (
            <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4 }}>Zugang, Reports, Kasse, Exporte — in fünf Schritten.</div>
          )}
          {guideOpen && (
            <ol style={{ margin: "10px 0 0", paddingLeft: 20, fontSize: 13, lineHeight: 1.7, color: COLORS.text }}>
              <li><b>Zugang:</b> Der Salon lädt unter Buchhaltung → Steuerberater ein. Die Mail enthält Login-Adresse und Startpasswort. Nach dem ersten Login das Passwort unter „Mein Profil“ ändern.</li>
              <li><b>Termin-Liste:</b> Zeitraum wählen → alle Termine mit Mitarbeiter, Dauer, Brutto, Zahlstatus und Zahlungsart. „CSV-Export“ für Excel oder DATEV-Vorbereitung.</li>
              <li><b>Provisions-Report:</b> Monat wählen → Umsatz und Provision je Mitarbeiter/in laut Provisionssatz des Salons. Grundlage für die Lohnabrechnung, ebenfalls als CSV.</li>
              <li><b>Kasse:</b> „Kasse öffnen“ → Reiter <b>Export</b>: Kassenbuch &amp; DATEV-Export (Zeitraum, Konten laut DATEV-Einstellungen), DSFinV-K-Export fürs Finanzamt, Inventurlisten, Langzeit-Archiv (10 Jahre). Reiter <b>Meldedaten</b> für die Kassenmeldung. Reiter <b>Statistik</b> für Umsätze und Z-Bons. Alles nur lesend.</li>
              <li><b>Stundenzettel:</b> Die Arbeitszeiten der Mitarbeiter druckt der Salon unter Mitarbeiter → Zeiterfassung → „Stundenzettel drucken“ (eine Seite je Person und Monat, mit Unterschriftszeilen).</li>
            </ol>
          )}
          {guideOpen && (
            <div style={{ marginTop: 10, fontSize: 11, color: COLORS.textLight }}>Fragen: support@shearly.de</div>
          )}
        </Card>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, borderBottom: `1px solid ${COLORS.border}` }}>
        {[
          { id: "appointments", label: "Termin-Liste" },
          { id: "commissions", label: "Provisions-Report" },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: "10px 18px", border: "none", background: "transparent",
            color: tab === t.id ? COLORS.primary : COLORS.textLight,
            borderBottom: tab === t.id ? `3px solid ${COLORS.primary}` : "3px solid transparent",
            fontSize: 14, fontWeight: tab === t.id ? 700 : 500, cursor: "pointer", marginBottom: -1,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === "appointments" && (
        <Card>
          <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap", marginBottom: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>Von</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ padding: "8px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13, background: "var(--card)", color: COLORS.text }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>Bis</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={{ padding: "8px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13, background: "var(--card)", color: COLORS.text }} />
            </div>
            <Btn size="sm" onClick={loadAppts}>Anwenden</Btn>
            <Btn size="sm" variant="secondary" onClick={exportCsv}><Download size={12} /> CSV-Export</Btn>
          </div>

          {loading && <div style={{ padding: 20, textAlign: "center", color: COLORS.textLight }}>Lade…</div>}
          {!loading && apptData && apptData.error && <div style={{ padding: 12, color: COLORS.danger }}>⚠ {apptData.error}</div>}
          {!loading && apptData && !apptData.error && (
            <>
              <div style={{ fontSize: 12, color: COLORS.textLight, marginBottom: 10 }}>
                {apptData.total} Termine im Zeitraum · Summe Brutto: <strong>{apptData.items.reduce((s,a) => s + (a.total_eur || 0), 0).toFixed(2)} €</strong>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                      {["Datum","Uhrzeit","Mitarbeiter","Dauer","Brutto","Status","Zahlung"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.textLight, fontWeight: 600, fontSize: 11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {apptData.items.slice(0,500).map(a => (
                      <tr key={a.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                        <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 12 }}>{a.date}</td>
                        <td style={{ padding: "8px 10px" }}>{a.start_time}</td>
                        <td style={{ padding: "8px 10px" }}>{a.employee || "—"}</td>
                        <td style={{ padding: "8px 10px" }}>{a.duration_min} Min</td>
                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>{a.total_eur.toFixed(2)} €</td>
                        <td style={{ padding: "8px 10px" }}>
                          <Badge text={a.status === "cancelled" ? "Storniert" : a.status === "confirmed" ? "Bestätigt" : a.status}
                                 color={a.status === "cancelled" ? COLORS.danger : COLORS.success} />
                        </td>
                        <td style={{ padding: "8px 10px", fontSize: 12 }}>
                          {a.payment_status === "paid" ? `✓ ${a.payment_method || "—"}` : "Offen"}
                        </td>
                      </tr>
                    ))}
                    {apptData.items.length === 0 && (
                      <tr><td colSpan="7" style={{ padding: 30, textAlign: "center", color: COLORS.textLight }}>Keine Termine im Zeitraum.</td></tr>
                    )}
                  </tbody>
                </table>
                {apptData.items.length > 500 && (
                  <div style={{ marginTop: 8, padding: 8, fontSize: 11, color: COLORS.textLight, textAlign: "center" }}>
                    Zeige 500 von {apptData.items.length} Treffern. Für mehr CSV-Export nutzen.
                  </div>
                )}
              </div>
            </>
          )}
        </Card>
      )}

      {tab === "commissions" && (
        <Card>
          <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap", marginBottom: 16 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>Monat</label>
              <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ padding: "8px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`, fontSize: 13, background: "var(--card)", color: COLORS.text }} />
            </div>
            <Btn size="sm" onClick={loadComm}>Laden</Btn>
            <Btn size="sm" variant="secondary" onClick={exportCsv}><Download size={12} /> CSV-Export</Btn>
          </div>

          {loading && <div style={{ padding: 20, textAlign: "center", color: COLORS.textLight }}>Lade…</div>}
          {!loading && commData && commData.error && <div style={{ padding: 12, color: COLORS.danger }}>⚠ {commData.error}</div>}
          {!loading && commData && !commData.error && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12, marginBottom: 16 }}>
                <StatCard icon={Euro} label="Umsatz gesamt" value={`${(commData.total_eur || 0).toFixed(2)} €`} color={COLORS.success} />
                <StatCard icon={Users} label="Provisionen gesamt" value={`${(commData.total_commission_eur || 0).toFixed(2)} €`} color={COLORS.primary} />
                <StatCard icon={BarChart3} label="Mitarbeiter" value={commData.items.length} color={COLORS.info} />
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                      {["Mitarbeiter","Termine","Umsatz","Satz %","Provision"].map(h => (
                        <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.textLight, fontWeight: 600, fontSize: 11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {commData.items.map(c => (
                      <tr key={c.employee_id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                        <td style={{ padding: "8px 10px", fontWeight: 600 }}>{c.name}</td>
                        <td style={{ padding: "8px 10px" }}>{c.appointments}</td>
                        <td style={{ padding: "8px 10px" }}>{c.total_eur.toFixed(2)} €</td>
                        <td style={{ padding: "8px 10px" }}>{c.commission_rate_pct}%</td>
                        <td style={{ padding: "8px 10px", fontWeight: 700, color: COLORS.primary }}>{c.commission_eur.toFixed(2)} €</td>
                      </tr>
                    ))}
                    {commData.items.length === 0 && (
                      <tr><td colSpan="5" style={{ padding: 30, textAlign: "center", color: COLORS.textLight }}>Keine Mitarbeiter mit Provisionen.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
};


// TAX_CONSULTANT_v1 -- Owner-side: Steuerberater einladen + verwalten
export const TaxConsultantManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => {
    setLoading(true);
    api.request("/owner/tax-consultant").then(r => {
      if (r && r.ok) setItems(r.data?.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(load, []);

  const invite = async () => {
    setBusy(true); setMsg(null);
    const r = await api.request("/owner/tax-consultant", {
      method: "POST",
      body: JSON.stringify({ email: newEmail.trim(), name: newName.trim(), label: newLabel.trim() }),
    });
    setBusy(false);
    if (r && r.ok) {
      setMsg({ type: "ok", text: r.data.message, password: r.data.password, mail_sent: r.data.mail_sent });
      setNewEmail(""); setNewName(""); setNewLabel("");
      setAdding(false);
      load();
    } else {
      setMsg({ type: "err", text: (r && r.data && r.data.error) || "Fehler beim Einladen" });
    }
  };
  const revoke = async (id, name) => {
    if (!confirm(`Steuerberater "${name}" Zugang sperren?\nDer Login wird sofort deaktiviert.`)) return;
    const r = await api.request(`/owner/tax-consultant/${id}`, { method: "DELETE" });
    if (r && r.ok) load();
    else alert("Fehler: " + ((r && r.data && r.data.error) || "Unbekannt"));
  };

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, gap: 8, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Steuerberater-Zugang</h3>
        {!adding && <Btn size="sm" onClick={() => setAdding(true)}><Plus size={12} /> Einladen</Btn>}
      </div>

      {loading && <div style={{ padding: 12, color: COLORS.textLight, fontSize: 13 }}>Lade…</div>}

      {!loading && items.length === 0 && !adding && (
        <div style={{ fontSize: 12, color: COLORS.textLight, padding: "8px 0" }}>
          Noch kein Steuerberater eingeladen. Klicke „Einladen" um Zugang zu vergeben.
        </div>
      )}

      {items.map(it => (
        <div key={it.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{it.name || it.email}{it.label ? <span style={{ marginLeft: 6, fontWeight: 400, color: COLORS.textLight, fontSize: 11 }}>· {it.label}</span> : null}</div>
            <div style={{ fontSize: 11, color: COLORS.textLight, fontFamily: "monospace" }}>{it.email}</div>
            <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 2 }}>
              {it.active ? (it.last_login ? `Aktiv · letzter Login ${new Date(it.last_login).toLocaleDateString("de-DE")}` : "Aktiv · noch nicht eingeloggt") : "Gesperrt"}
            </div>
          </div>
          {it.active && (
            <button onClick={() => revoke(it.id, it.name || it.email)} style={{
              padding: "4px 10px", borderRadius: 6, border: "1px solid " + COLORS.danger,
              background: "transparent", color: COLORS.danger, fontSize: 11, fontWeight: 600, cursor: "pointer"
            }}>Sperren</button>
          )}
        </div>
      ))}

      {adding && (
        <div style={{ marginTop: 12, padding: 12, background: "var(--cardHover)", borderRadius: 8, display: "flex", flexDirection: "column", gap: 8 }}>
          <Input label="E-Mail (Login)" type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="stb@kanzlei-xy.de" />
          <Input label="Name" value={newName} onChange={e => setNewName(e.target.value)} placeholder="z.B. Klaus Müller" />
          <Input label="Bezeichnung (optional)" value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="z.B. Kanzlei Müller GmbH" />
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={invite} disabled={busy || !newEmail || !newEmail.includes("@")}>
              {busy ? "Erstelle…" : "Einladen"}
            </Btn>
            <Btn variant="secondary" onClick={() => { setAdding(false); setMsg(null); }} disabled={busy}>Abbrechen</Btn>
          </div>
          <div style={{ fontSize: 10, color: COLORS.textLight, lineHeight: 1.55 }}>
            Wir erstellen einen Read-Only-Login. Login-Daten werden per E-Mail an den Steuerberater versandt.
          </div>
        </div>
      )}

      {msg && (
        <div style={{
          marginTop: 10, padding: 10, borderRadius: 6, fontSize: 12, lineHeight: 1.55,
          background: msg.type === "ok" ? "rgba(16,185,129,0.10)" : "rgba(239,68,68,0.10)",
          color: msg.type === "ok" ? "#065F46" : "#991B1B",
          borderLeft: `3px solid ${msg.type === "ok" ? COLORS.success : COLORS.danger}`,
        }}>
          {msg.type === "ok" ? "✓ " : "✗ "}{msg.text}
          {msg.password && (
            <div style={{ marginTop: 8, padding: 8, background: "var(--card)", borderRadius: 4, fontFamily: "monospace", fontSize: 11 }}>
              Falls Mail nicht ankommt: Passwort = <strong>{msg.password}</strong>
              <button onClick={() => navigator.clipboard.writeText(msg.password)} style={{ marginLeft: 8, padding: "2px 8px", fontSize: 10, border: "1px solid " + COLORS.border, background: "var(--cardHover)", color: "var(--text)", borderRadius: 4, cursor: "pointer" }}>Kopieren</button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};


// PAGE: Accounting (Buchhaltung)
// ============================================================
export const AccountingPage = ({ appointments, customers }) => {
  const [taxAdvisorAccess, setTaxAdvisorAccess] = useState(true);
  const [taxData, setTaxData] = useState(null);
  const [editingTax, setEditingTax] = useState(false);
  const [taxForm, setTaxForm] = useState({});
  const [savingTax, setSavingTax] = useState(false);
  useEffect(() => {
    api.request("/salon/tax-settings").then(res => {
      if (res.ok) { setTaxData(res.data); setTaxForm(res.data || {}); }
    });
  }, []);
  const saveTax = async () => {
    setSavingTax(true);
    const res = await api.request("/salon/tax-settings", { method: "PUT", body: taxForm });
    setSavingTax(false);
    if (res.ok) { setTaxData(taxForm); setEditingTax(false); }
    else alert(res.data?.error || "Speichern fehlgeschlagen");
  };
  const paid = appointments.filter(a => a.paymentStatus === "paid");
  const totalNet = Math.round(paid.reduce((s, a) => s + a.totalPrice, 0) / 1.19);
  const totalVat = paid.reduce((s, a) => s + a.totalPrice, 0) - totalNet;

  const invoices = [
    { id: "RE-2026-0047", date: "2026-03-09", customer: "Sophie Klein", amount: 144, vat: 22.99, status: "paid", method: "Stripe" },
    { id: "RE-2026-0046", date: "2026-03-09", customer: "Maria Wagner", amount: 90, vat: 14.37, status: "paid", method: "Karte (POS)" },
    { id: "RE-2026-0045", date: "2026-03-09", customer: "Klaus Hoffmann", amount: 46, vat: 7.34, status: "open", method: "—" },
    { id: "RE-2026-0044", date: "2026-03-08", customer: "Anna Becker", amount: 107, vat: 17.08, status: "paid", method: "Bar" },
    { id: "RE-2026-0043", date: "2026-03-07", customer: "Peter Braun", amount: 28, vat: 4.47, status: "paid", method: "Stripe" },
  ];

  return (
    <div>
      <Header title="Buchhaltung" subtitle="Termin-Übersicht & Stammdaten — DATEV läuft über dein Kassensystem" />

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: window.innerWidth < 768 ? 8 : 16, marginBottom: window.innerWidth < 768 ? 16 : 24 }}>
        <StatCard icon={Euro} label="Umsatz (Monat, brutto)" value={`${paid.reduce((s, a) => s + a.totalPrice, 0)} €`} color={COLORS.success} />
        <StatCard icon={Euro} label="Netto" value={`${totalNet} €`} color={COLORS.info} />
        <StatCard icon={BarChart3} label="USt. 19%" value={`${Math.round(totalVat)} €`} color={COLORS.warning} />
        <StatCard icon={CheckCircle} label="Rechnungen (Monat)" value={invoices.length} color={COLORS.primary} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "2fr 1fr", gap: 16, marginBottom: 24 }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Rechnungen</h3>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn size="sm" variant="secondary" title="Termine + Services + Preise als CSV (kein DATEV-Format — das macht deine Kasse)"
                onClick={() => {
                  /* ACCT_HONEST_v1 -- Termin-CSV-Export: Datum, Kunde, Service, Mitarbeiter, Brutto, MwSt */
                  const ym = new Date().toISOString().slice(0, 7);
                  const rows = (invoices || []).map(inv => [inv.date, inv.id, inv.customer, inv.amount, inv.vat.toFixed(2), inv.status, inv.method]);
                  const csv = ["Datum;Belegnr;Kunde;Brutto;USt;Status;Zahlweg", ...rows.map(r => r.join(";"))].join("\n");
                  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `shearly_termine_${ym}.csv`; a.click();
                  URL.revokeObjectURL(url);
                }}>
                <Download size={12} /> Termin-Report (CSV)
              </Btn>
              <Btn size="sm"><Plus size={12} /> Neue Rechnung</Btn>
            </div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                {["Nr.", "Datum", "Kunde", "Brutto", "USt.", "Status", "Zahlweg"].map(h => (
                  <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.textLight, fontWeight: 600, fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.map(inv => (
                <tr key={inv.id} style={{ borderBottom: `1px solid ${COLORS.border}`, transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = "var(--cardHover)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "10px 10px", fontFamily: "monospace", fontSize: 12, fontWeight: 600 }}>{inv.id}</td>
                  <td style={{ padding: "10px 10px" }}>{inv.date}</td>
                  <td style={{ padding: "10px 10px" }}>{inv.customer}</td>
                  <td style={{ padding: "10px 10px", fontWeight: 600 }}>{inv.amount} €</td>
                  <td style={{ padding: "10px 10px" }}>{inv.vat.toFixed(2)} €</td>
                  <td style={{ padding: "10px 10px" }}>
                    <Badge text={inv.status === "paid" ? "Bezahlt" : "Offen"} color={inv.status === "paid" ? COLORS.success : COLORS.warning} />
                  </td>
                  <td style={{ padding: "10px 10px" }}>{inv.method}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

        <div>
          <TaxConsultantManager />
          <Card style={{ marginBottom: 16 }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>Wichtig zu wissen</h3>
            <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6, background: "var(--cardHover)", padding: 10, borderRadius: 6, borderLeft: "3px solid " + COLORS.info }}>
              Buchhaltungs- und steuerrelevante Daten (Bar/Karte-Belege, TSE-Signatur, DATEV-Export, USt.-Voranmeldung) kommen aus deinem <em>Kassen-System</em> (Bcassa, Tillhub, SumUp).
              <br/><br/>
              Shearly liefert deinem Steuerberater nur Termin- und Service-Daten — als Plausibilitäts-Quelle gegen die Kasse und für Provisions-/Lohn-Berechnung.
            </div>
          </Card>

          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Steuer-Übersicht</h3>
              {!editingTax && (
                <button onClick={() => { setTaxForm(taxData || {}); setEditingTax(true); }} style={{ border: "1px solid " + COLORS.border, background: "var(--card)", color: "var(--text)" /* DARK_BTN_TEXT_v1: <button> erbt die Schriftfarbe nicht -> im Dunkelmodus schwarz auf dunkel */, borderRadius: 6, padding: "4px 10px", fontSize: 12, cursor: "pointer" }}>Bearbeiten</button>
              )}
            </div>
            {editingTax ? (
              <div style={{ display: "grid", gap: 10 }}>
                <Input label="USt.-Satz (%)" type="number" value={taxForm.tax_rate ?? ""} onChange={e => setTaxForm({ ...taxForm, tax_rate: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={!!taxForm.is_kleinunternehmer} onChange={e => setTaxForm({ ...taxForm, is_kleinunternehmer: e.target.checked })} />
                  Kleinunternehmerregelung (§19 UStG)
                </label>
                <Input label="USt.-ID" value={taxForm.vat_id || ""} onChange={e => setTaxForm({ ...taxForm, vat_id: e.target.value })} placeholder="DE123456789" />
                <Input label="Steuernummer" value={taxForm.tax_number || ""} onChange={e => setTaxForm({ ...taxForm, tax_number: e.target.value })} placeholder="z.B. 123/456/78901" />
                {/* POS_TILLHUB_v1 -- Hinweis dass DATEV erst mit Kasse vollständig wird */}
                <div style={{ padding: "8px 12px", background: "var(--cardHover)", borderRadius: 8, fontSize: 11, color: COLORS.textLight, marginBottom: 4 }}>
                  ℹ️ DATEV-Felder werden erst mit verbundener <strong>Kassensystem-Anbindung</strong> sinnvoll genutzt. Bis dahin nur als Stammdaten gespeichert.
                </div>
                <Input label="DATEV-Berater-Nr." value={taxForm.datev_consultant_number || ""} onChange={e => setTaxForm({ ...taxForm, datev_consultant_number: e.target.value })} />
                <Input label="DATEV-Mandanten-Nr." value={taxForm.datev_client_number || ""} onChange={e => setTaxForm({ ...taxForm, datev_client_number: e.target.value })} />
                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <Btn onClick={saveTax} disabled={savingTax}><Check size={14} /> {savingTax ? "Speichern..." : "Speichern"}</Btn>
                  <button onClick={() => { setEditingTax(false); setTaxForm(taxData || {}); }} style={{ border: "1px solid " + COLORS.border, background: "var(--card)", color: "var(--text)", borderRadius: 6, padding: "6px 12px", fontSize: 13, cursor: "pointer" }}>Abbrechen</button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, lineHeight: 2.2 }}>
                <div><strong>USt.-Satz:</strong> {taxData?.tax_rate != null ? `${taxData.tax_rate}%` : <em style={{ color: COLORS.muted }}>Nicht hinterlegt</em>}</div>
                <div><strong>Kleinunternehmerregelung:</strong> {taxData ? (taxData.is_kleinunternehmer ? "Ja" : "Nein") : <em style={{ color: COLORS.muted }}>—</em>}</div>
                <div><strong>USt.-ID:</strong> {taxData?.vat_id || <em style={{ color: COLORS.muted }}>Nicht hinterlegt</em>}</div>
                <div><strong>Steuernummer:</strong> {taxData?.tax_number || <em style={{ color: COLORS.muted }}>Nicht hinterlegt</em>}</div>
                <div><strong>DATEV-Berater-Nr.:</strong> {taxData?.datev_consultant_number || <em style={{ color: COLORS.muted }}>Nicht hinterlegt</em>}</div>
                <div><strong>DATEV-Mandanten-Nr.:</strong> {taxData?.datev_client_number || <em style={{ color: COLORS.muted }}>Nicht hinterlegt</em>}</div>
              </div>
            )}
            <div style={{ marginTop: 12, padding: 10, background: `${COLORS.info}08`, borderRadius: 8, fontSize: 12 }}>
              <strong>DATEV + USt.-Voranmeldung:</strong> laufen über dein Kassen-System (Bcassa, Tillhub, SumUp). Stammdaten oben (Berater-Nr., Mandanten-Nr., USt.-ID) sind hier nur zur Information hinterlegt — der Steuerberater erhält die Buchungs-Daten von deinem Kassen-Anbieter, nicht von uns.
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// PAGE: Gutscheine & Geschenkkarten
// ============================================================
// VOUCHERS_WIRE_v1 -- vorher komplett Mock (hardcodierte Liste, Buttons ohne onClick, kein Speichern).
// Jetzt: echte Liste (api.getVouchers) + Erstellen (api.createVoucher). Einlösung beim Bezahlen hat
// noch keinen Backend-Endpoint -> ehrlicher "kommt bald"-Hinweis statt totem Button.
export const VouchersPage = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [createType, setCreateType] = useState("Geschenkkarte");
  const [form, setForm] = useState({ value: "", buyer_name: "", recipient_name: "", expires: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    setLoading(true);
    const res = await api.getVouchers();
    if (res && res.ok && Array.isArray(res.data)) setVouchers(res.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const fmtType = (t) => (t === "gift_card" || t === "Geschenkkarte") ? "Geschenkkarte" : "Rabatt-Gutschein";
  const remOf = (v) => Number(v.remaining != null ? v.remaining : v.value || 0);
  const activeList = vouchers.filter(v => remOf(v) > 0);
  const totalActive = activeList.reduce((s, v) => s + remOf(v), 0);

  const openCreate = (type) => { setCreateType(type); setForm({ value: "", buyer_name: "", recipient_name: "", expires: "" }); setMsg(""); setShowCreate(true); };
  const createVoucher = async () => {
    if (!form.value || Number(form.value) <= 0) { setMsg("Bitte einen Wert eingeben."); return; }
    setSaving(true); setMsg("");
    const res = await api.createVoucher({
      type: createType === "Geschenkkarte" ? "gift_card" : "discount",
      value: Number(form.value),
      buyer_name: form.buyer_name || null,
      recipient_name: form.recipient_name || null,
      expires: form.expires || null,
    });
    setSaving(false);
    if (res && res.ok) { setShowCreate(false); await load(); }
    else { setMsg("Fehler: " + ((res && res.data && res.data.error) || "konnte nicht erstellt werden")); }
  };

  return (
    <div>
      <Header title="Gutscheine & Geschenkkarten" subtitle="Verkauf & Verwaltung" />
      <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "repeat(3, 1fr)" : "repeat(3, 1fr)", gap: window.innerWidth < 768 ? 8 : 16, marginBottom: window.innerWidth < 768 ? 16 : 24 }}>
        <StatCard icon={Star} label="Aktive Gutscheine" value={activeList.length} color={COLORS.primary} />
        <StatCard icon={Euro} label="Offenes Guthaben" value={`${totalActive} €`} color={COLORS.accent} />
        <StatCard icon={TrendingUp} label="Gesamt" value={vouchers.length} color={COLORS.info} />
      </div>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>Gutschein-Übersicht</h3>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn size="sm" onClick={() => openCreate("Geschenkkarte")}><Plus size={12} /> Geschenkkarte erstellen</Btn>
            <Btn size="sm" variant="secondary" onClick={() => openCreate("Rabatt-Gutschein")}><Plus size={12} /> Rabatt-Gutschein</Btn>
          </div>
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: 20, color: COLORS.textLight, fontSize: 13 }}>Laden...</div>
        ) : vouchers.length === 0 ? (
          <div style={{ textAlign: "center", padding: 24, color: COLORS.textLight, fontSize: 13 }}>Noch keine Gutscheine. Erstelle den ersten oben rechts.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 560 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${COLORS.border}` }}>
                  {["Code", "Typ", "Wert", "Restguthaben", "Empfänger", "Gültig bis"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "8px 10px", color: COLORS.textLight, fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {vouchers.map(v => (
                  <tr key={v.id || v.code} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "10px 10px", fontFamily: "monospace", fontWeight: 600 }}>{v.code}</td>
                    <td style={{ padding: "10px 10px" }}><Badge text={fmtType(v.type)} color={fmtType(v.type) === "Geschenkkarte" ? COLORS.primary : COLORS.accent} /></td>
                    <td style={{ padding: "10px 10px", fontWeight: 600 }}>{v.value} €</td>
                    <td style={{ padding: "10px 10px", fontWeight: 700, color: remOf(v) > 0 ? COLORS.success : COLORS.textLight }}>{remOf(v)} €</td>
                    <td style={{ padding: "10px 10px" }}>{v.recipient_name || "—"}</td>
                    <td style={{ padding: "10px 10px" }}>{v.expires || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card style={{ marginTop: 16 }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700 }}>Gutschein einlösen</h3>
        <p style={{ fontSize: 12, color: COLORS.textLight, margin: 0, lineHeight: 1.7 }}>Die Einlösung beim Bezahlen wird in einer der nächsten Versionen ergänzt. Aktuell kannst du Gutscheine erstellen und verwalten.</p>
      </Card>

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title={createType + " erstellen"} width={460}>
        <Input label="Wert (€)" type="number" value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} placeholder="50" />
        <Input label="Käufer (optional)" value={form.buyer_name} onChange={e => setForm(f => ({ ...f, buyer_name: e.target.value }))} />
        <Input label="Empfänger (optional)" value={form.recipient_name} onChange={e => setForm(f => ({ ...f, recipient_name: e.target.value }))} />
        <Input label="Gültig bis (optional)" type="date" value={form.expires} onChange={e => setForm(f => ({ ...f, expires: e.target.value }))} />
        {msg && <div style={{ fontSize: 12, color: COLORS.danger, marginBottom: 8 }}>{msg}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <Btn onClick={createVoucher} disabled={saving}>{saving ? "..." : "Erstellen"}</Btn>
          <Btn variant="secondary" onClick={() => setShowCreate(false)}>Abbrechen</Btn>
        </div>
      </Modal>
    </div>
  );
};